import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, TrendingUp, Award, Target, Shield, Zap, Check } from 'lucide-react';
import { AboutHero } from '../components/PageHeroes';
import '../styles/About.css';
import aboutBg from '../images/about-bg.jpg';
import storyBg from '../images/story-bg.jpg';

const About = () => {
  const stats = [
    { number: '500+', label: 'Happy Clients', icon: Users },
    { number: '1000+', label: 'Payroll Records', icon: TrendingUp },
    { number: '99.9%', label: 'Uptime', icon: Award },
    { number: '24/7', label: 'Support', icon: Target }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'Bank-level encryption and security across payroll, invoicing, and every financial record.',
      color: '#0ea5e9'
    },
    {
      icon: Award,
      title: 'Compliance',
      description: '100% HMRC and GDPR compliant, with automatic updates as rules change.',
      color: '#10b981'
    },
    {
      icon: Zap,
      title: 'Reliability',
      description: '99.9% uptime ensures payroll, invoicing, and every process keeps running.',
      color: '#f59e0b'
    },
    {
      icon: Users,
      title: 'Support',
      description: '24/7 dedicated support across your whole business, not just payroll.',
      color: '#06b6d4'
    }
  ];

  const visionPoints = [
    'Running a business is simple, secure, and compliant — payroll, finance, and HR alike',
    'Every UK business stays HMRC and GDPR compliant effortlessly',
    'Teams spend less time switching between tools and admin, more on strategy',
    'Technology serves businesses, not the other way around'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      className="about-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <AboutHero />

      {/* Story Section */}
      <section className="about-story">
        <div className="about-container">
          <motion.div
            className="story-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="story-text">
              <p className="story-eyebrow">Founded 2020</p>
              <motion.h2 variants={itemVariants}>Our Story</motion.h2>
              <div className="story-paragraphs">
                <motion.p variants={itemVariants}>
                  Effective Global Staff started with payroll — and the simple mission to revolutionize how UK businesses manage it.
                </motion.p>
                <motion.p variants={itemVariants}>
                  We recognized the complexity and burden of HMRC compliance, so we built a platform that's not just secure and compliant, but genuinely easy to use. As our customers grew, so did the platform — invoicing, accounts payable, banking, accounting, VAT, and HR all followed, so businesses could run everything from one place instead of stitching together separate tools.
                </motion.p>
                <motion.p variants={itemVariants}>
                  Today, we're trusted by hundreds of businesses across the UK, from small startups to growing enterprises, for payroll and everything that surrounds it.
                </motion.p>
              </div>
            </div>

            <motion.div
              className="story-visual"
              whileHover={{ y: -6 }}
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div
                className="visual-box"
                style={{
                  backgroundImage: `linear-gradient(135deg, rgba(14,165,233,0.75) 0%, rgba(6,182,212,0.75) 100%), url(${storyBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
              <p className="visual-caption">Building the future of business management, starting with payroll</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats">
        <div className="about-container">
          <motion.div
            className="stats-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={idx}
                  className="stat-item"
                  variants={itemVariants}
                  whileHover={{ y: -6 }}
                >
                  <div className="stat-icon">
                    <Icon size={24} />
                  </div>
                  <h3>{stat.number}</h3>
                  <p>{stat.label}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="about-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="section-eyebrow">What we stand for</p>
            <h2>Our Core Values</h2>
          </motion.div>

          <motion.div
            className="values-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={idx}
                  className="value-card"
                  variants={itemVariants}
                  whileHover={{ y: -6 }}
                  style={{ '--value-color': value.color }}
                >
                  <div className="value-icon">
                    <Icon size={22} color={value.color} />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="about-vision">
        <div className="about-container">
          <motion.div
            className="vision-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="vision-text"
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="section-eyebrow">Looking ahead</p>
              <h2>Our Vision</h2>
              <p className="vision-lead">
                We envision a world where running a business — payroll, finance, and HR alike — is secure, compliant, and effortless for every UK business.
              </p>

              <ul className="vision-list">
                {visionPoints.map((point, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.08 }}
                  >
                    <Check size={18} className="vision-check" />
                    <span>{point}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="vision-visual"
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -6 }}
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(14,165,233,0.8) 0%, rgba(6,182,212,0.8) 100%), url(${aboutBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <div className="vision-visual-inner">
                <h3>Beyond Payroll Innovation</h3>
                <p>Making HMRC and GDPR compliance simple and accessible across your whole business</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-cta-bg" />
        <div className="about-container">
          <motion.div
            className="about-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Simplify Your Whole Business?</h2>
            <p>Join hundreds of UK businesses managing payroll, finance, and operations securely and compliantly</p>
            <Link to="/contact" className="btn btn-secondary">
              Get Started Today <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;